import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  GraduationCap, Play, BookOpen, CheckCircle, 
  ChevronRight, BarChart3, Award, Download, Medal, ExternalLink
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Academy = () => {

  const navigate = useNavigate();
  const [cursos, setCursos] = useState([]);
  const [progressoGeral, setProgressoGeral] = useState([]);
  const [certificados, setCertificados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cursos');
  
  const token = localStorage.getItem('tayane_token');
  const { t } = useLanguage();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cursosRes, certificadosRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/academy/cursos`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/academy/certificados`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCursos(Array.isArray(cursosRes.data) ? cursosRes.data : (cursosRes.data?.cursos || cursosRes.data?.items || cursosRes.data?.data || []));
      setCertificados(Array.isArray(certificadosRes.data) ? certificadosRes.data : (certificadosRes.data?.certificados || certificadosRes.data?.items || certificadosRes.data?.data || []));
      
      // Buscar progresso geral de cada curso
      const cursosArr = Array.isArray(cursosRes.data) ? cursosRes.data : (cursosRes.data?.cursos || cursosRes.data?.items || cursosRes.data?.data || []);
      const progressoPromises = cursosArr.map(async (curso) => {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/academy/cursos/${curso.id}/progresso-geral`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          return res.data;
        } catch {
          return null;
        }
      });
      
      const progressos = await Promise.all(progressoPromises);
      setProgressoGeral(progressos.filter(p => p !== null));
      
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      if (error.response?.status !== 404) {
        toast.error("Erro ao carregar dados");
      }
    } finally {
      setLoading(false);
    }
  };

  const getProgressoByCurso = (cursoId) => {
    return progressoGeral.find(p => p.curso_id === cursoId) || { 
      percentual_geral: 0, 
      percentual_aulas: 0,
      percentual_questionarios: 0,
      aulas_concluidas: 0, 
      total_aulas: 0,
      questionarios_aprovados: 0,
      total_questionarios: 0,
      concluido: false,
      certificado_disponivel: false
    };
  };

  const gerarCertificado = async (cursoId) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/academy/cursos/${cursoId}/certificado`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Certificado gerado com sucesso!");
      setCertificados([response.data, ...certificados]);
      
      // Atualizar progresso para mostrar certificado_id
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao gerar certificado");
    }
  };

  const statsData = {
    cursos: cursos.length,
    concluidos: progressoGeral.filter(p => p.concluido).length,
    emAndamento: progressoGeral.filter(p => p.percentual_geral > 0 && !p.concluido).length,
    progressoMedio: progressoGeral.length > 0 
      ? Math.round(progressoGeral.reduce((acc, p) => acc + p.percentual_geral, 0) / progressoGeral.length)
      : 0
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900">
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900">
      
      <div className="flex-1 p-4 lg:p-8 overflow-auto pt-20 lg:pt-8">
        <div className="max-w-7xl mx-auto space-y-6 lg:space-y-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl lg:text-4xl font-heading font-bold text-slate-800 dark:text-slate-100">
                Academy
              </h1>
              <p className="text-sm lg:text-base text-slate-600 dark:text-slate-300 mt-1 lg:mt-2">
                Treinamentos e capacitação da equipe
              </p>
            </div>
            
            {/* Tabs */}
            <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('cursos')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'cursos'
                    ? 'bg-white dark:bg-slate-700 text-primary shadow'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                <BookOpen className="w-4 h-4 inline mr-2" />
                Cursos
              </button>
              <button
                onClick={() => setActiveTab('certificados')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'certificados'
                    ? 'bg-white dark:bg-slate-700 text-primary shadow'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                <Award className="w-4 h-4 inline mr-2" />
                Certificados
                {certificados.length > 0 && (
                  <span className="ml-2 bg-green-500 text-white px-1.5 py-0.5 rounded-full text-xs">
                    {certificados.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="glass-panel dark:bg-slate-900/80 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{statsData.cursos}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Cursos</p>
                </div>
              </div>
            </div>
            <div className="glass-panel dark:bg-slate-900/80 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{statsData.concluidos}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t("Concluídos")}</p>
                </div>
              </div>
            </div>
            <div className="glass-panel dark:bg-slate-900/80 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Play className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{statsData.emAndamento}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t("Em Andamento")}</p>
                </div>
              </div>
            </div>
            <div className="glass-panel dark:bg-slate-900/80 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{statsData.progressoMedio}%</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t("Progresso Geral")}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'cursos' ? (
            <div className="glass-panel dark:bg-slate-900/80 rounded-2xl p-4 lg:p-6">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
                Cursos Disponíveis
              </h2>
              
              {cursos.length === 0 ? (
                <div className="text-center py-12">
                  <GraduationCap className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-600 dark:text-slate-400">{t("Nenhum curso disponível ainda")}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-500 mt-2">
                    Os administradores podem criar cursos na área de gerenciamento
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cursos.map((curso) => {
                    const prog = getProgressoByCurso(curso.id);
                    return (
                      <div
                        key={curso.id}
                        onClick={() => navigate(`/crm/academy/curso/${curso.id}`)}
                        className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-lg hover:border-primary/30 transition-all cursor-pointer group"
                        data-testid={`curso-card-${curso.id}`}
                      >
                        {/* Imagem de capa */}
                        <div className="h-32 bg-gradient-to-br from-primary/20 to-primary/40 relative overflow-hidden">
                          {curso.imagem_capa ? (
                            <img 
                              src={curso.imagem_capa.startsWith('/api') ? `${BACKEND_URL}${curso.imagem_capa}` : curso.imagem_capa}
                              alt={curso.titulo}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <GraduationCap className="w-12 h-12 text-primary/50" />
                            </div>
                          )}
                          {prog.concluido && (
                            <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                              <Award className="w-3 h-3" />
                              Concluído
                            </div>
                          )}
                        </div>
                        
                        {/* Conteúdo */}
                        <div className="p-4">
                          <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-1 group-hover:text-primary transition-colors">
                            {curso.titulo}
                          </h3>
                          {curso.descricao && (
                            <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2 mb-3">
                              {curso.descricao}
                            </p>
                          )}
                          
                          {/* Info */}
                          <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400 mb-3">
                            <span className="flex items-center gap-1">
                              <BookOpen className="w-3 h-3" />
                              {curso.total_modulos} módulos
                            </span>
                            <span className="flex items-center gap-1">
                              <Play className="w-3 h-3" />
                              {curso.total_aulas} aulas
                            </span>
                          </div>
                          
                          {/* Barras de progresso detalhadas */}
                          <div className="space-y-2 mb-3">
                            {/* Progresso de Aulas */}
                            <div className="space-y-0.5">
                              <div className="flex justify-between text-[10px]">
                                <span className="text-slate-500 dark:text-slate-400">{t("Aulas")}</span>
                                <span className="font-medium text-slate-600 dark:text-slate-300">
                                  {prog.aulas_concluidas}/{prog.total_aulas}
                                </span>
                              </div>
                              <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-blue-500 rounded-full transition-all"
                                  style={{ width: `${prog.percentual_aulas}%` }}
                                />
                              </div>
                            </div>
                            
                            {/* Progresso de Questionários (se houver) */}
                            {prog.total_questionarios > 0 && (
                              <div className="space-y-0.5">
                                <div className="flex justify-between text-[10px]">
                                  <span className="text-slate-500 dark:text-slate-400">{t("Avaliações")}</span>
                                  <span className="font-medium text-slate-600 dark:text-slate-300">
                                    {prog.questionarios_aprovados}/{prog.total_questionarios}
                                  </span>
                                </div>
                                <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-purple-500 rounded-full transition-all"
                                    style={{ width: `${prog.percentual_questionarios}%` }}
                                  />
                                </div>
                              </div>
                            )}
                            
                            {/* Progresso Geral */}
                            <div className="space-y-0.5">
                              <div className="flex justify-between text-xs">
                                <span className="text-slate-600 dark:text-slate-400 font-medium">{t("Progresso Geral")}</span>
                                <span className="font-bold text-slate-700 dark:text-slate-200">
                                  {Math.round(prog.percentual_geral)}%
                                </span>
                              </div>
                              <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full transition-all ${
                                    prog.concluido 
                                      ? 'bg-green-500' 
                                      : prog.percentual_geral > 0 
                                      ? 'bg-primary' 
                                      : 'bg-slate-300 dark:bg-slate-600'
                                  }`}
                                  style={{ width: `${prog.percentual_geral}%` }}
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Botões */}
                          <div className="flex gap-2">
                            <button 
                              className="flex-1 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/crm/academy/curso/${curso.id}`);
                              }}
                            >
                              {prog.percentual_geral === 0 ? 'Começar' : prog.concluido ? 'Revisar' : 'Continuar'}
                              <ChevronRight className="w-4 h-4" />
                            </button>
                            
                            {prog.certificado_disponivel && !prog.certificado_id && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  gerarCertificado(curso.id);
                                }}
                                className="px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium flex items-center gap-1 transition-colors"
                                title="Gerar Certificado"
                              >
                                <Medal className="w-4 h-4" />
                              </button>
                            )}
                            
                            {prog.certificado_id && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveTab('certificados');
                                }}
                                className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium flex items-center gap-1 transition-colors"
                                title="Ver Certificado"
                              >
                                <Award className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            /* Tab Certificados */
            <div className="glass-panel dark:bg-slate-900/80 rounded-2xl p-4 lg:p-6">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
                Meus Certificados
              </h2>
              
              {certificados.length === 0 ? (
                <div className="text-center py-12">
                  <Award className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-600 dark:text-slate-400">{t("Nenhum certificado ainda")}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-500 mt-2">
                    Complete cursos para receber certificados de conclusão
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {certificados.map((cert) => (
                    <div
                      key={cert.id}
                      className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/10 rounded-xl border-2 border-amber-200 dark:border-amber-700/50 overflow-hidden"
                    >
                      {/* Header decorativo */}
                      <div className="bg-gradient-to-r from-amber-500 to-yellow-500 p-4 text-center">
                        <Medal className="w-10 h-10 text-white mx-auto mb-2" />
                        <h3 className="text-lg font-bold text-white">{t("Certificado de Conclusão")}</h3>
                      </div>
                      
                      {/* Conteúdo */}
                      <div className="p-6 text-center">
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                          Certificamos que
                        </p>
                        <p className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                          {cert.user_name}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                          concluiu com êxito o curso
                        </p>
                        <p className="text-lg font-semibold text-primary mb-4">
                          {cert.curso_titulo}
                        </p>
                        
                        {/* Detalhes */}
                        <div className="flex justify-center gap-4 text-xs text-slate-500 dark:text-slate-400 mb-4">
                          {cert.nota_media && (
                            <span className="flex items-center gap-1">
                              <BarChart3 className="w-3 h-3" />
                              Nota: {cert.nota_media}%
                            </span>
                          )}
                          {cert.horas_curso && (
                            <span className="flex items-center gap-1">
                              <Play className="w-3 h-3" />
                              {cert.horas_curso}h
                            </span>
                          )}
                        </div>
                        
                        {/* Data e código */}
                        <p className="text-xs text-slate-400 dark:text-slate-500 mb-1">
                          Emitido em {new Date(cert.data_conclusao).toLocaleDateString('pt-BR')}
                        </p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 font-mono">
                          Código: {cert.codigo_verificacao}
                        </p>
                      </div>
                      
                      {/* Ações */}
                      <div className="px-6 pb-6 flex gap-2">
                        <button
                          onClick={() => window.open(`${BACKEND_URL}/api/academy/certificados/verificar/${cert.codigo_verificacao}`, '_blank')}
                          className="flex-1 py-2 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Verificar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Academy;
